import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import { discoveryKeys } from './discovery';
import { newIdempotencyKey } from './engagement-requirements';

/**
 * Engagement -> Discovery -> Sessions, "Transcripts to place" (T-0261,
 * E23-design §5.2, §13): the placements the interview finder proposed for
 * transcripts that arrived without an interview, and the person's decision.
 *
 *   GET  …/discovery/interview-proposals                 the pending placements
 *   POST …/discovery/interview-proposals/{pid}:confirm   one transaction: the interview,
 *                                                        the attach and its reading
 *   POST …/discovery/interview-proposals/{pid}:decline   rejected; the file stays general
 *   POST …/discovery/interview-proposals:confirm         a batch, per-item outcomes
 *
 * Contract: `apps/api/src/services/discovery-interview-proposals.ts`. The
 * match basis in words, the confidence and the pre-selection come from the
 * server; the screen shows them. Every write carries an `Idempotency-Key`.
 */

const base = (engagementId: string) =>
  `/api/v1/engagements/${encodeURIComponent(engagementId)}/discovery/interview-proposals`;
const seg = encodeURIComponent;

export const PlacementPersonSchema = z.object({
  person_id: z.string().nullable(),
  full_name: z.string(),
  title: z.string().nullable(),
  speaker_label: z.string(),
});
export type PlacementPerson = z.infer<typeof PlacementPersonSchema>;

export const InterviewProposalSchema = z.object({
  proposal_id: z.string(),
  created_at: z.string(),
  confidence: z.number(),
  preselected: z.boolean(),
  document: z.object({
    id: z.string(),
    title: z.string(),
    version_id: z.string(),
    mime_type: z.string().nullable(),
    stale: z.boolean(),
  }),
  placement: z.enum(['attach', 'create']),
  version_kind: z.enum(['first', 'corrected']),
  match_basis: z.enum(['speaker_names', 'type_plan_row', 'title_keywords', 'ai', 'none']),
  match_text: z.string(),
  interview: z
    .object({ id: z.string(), code: z.string(), title: z.string(), status: z.string() })
    .nullable(),
  interview_type: z.object({ id: z.string(), name: z.string() }).nullable(),
  group_label: z.string(),
  areas: z.array(z.object({ id: z.string(), code: z.string(), label: z.string() })),
  people: z.array(PlacementPersonSchema),
  matched_keywords: z.array(z.string()),
  line_count: z.number(),
  speaker_count: z.number(),
});
export type InterviewProposal = z.infer<typeof InterviewProposalSchema>;

const InterviewProposalListSchema = z.object({ items: z.array(InterviewProposalSchema) });

export const ConfirmPlacementResultSchema = z.object({
  proposal_id: z.string(),
  status: z.enum(['accepted', 'overridden']),
  placement: z.enum(['attach', 'create']),
  interview: z.object({
    id: z.string(),
    code: z.string(),
    title: z.string(),
    status: z.literal('held'),
    created: z.boolean(),
  }),
  transcript_id: z.string(),
  run_id: z.string(),
  version_no: z.number(),
  scope: z.enum(['full', 'changed_only']),
  line_count: z.number(),
  people_created: z.number(),
});
export type ConfirmPlacementResult = z.infer<typeof ConfirmPlacementResultSchema>;

export const DECLINE_PLACEMENT_REASONS = ['not_an_interview', 'duplicate', 'other'] as const;
export type DeclinePlacementReason = (typeof DECLINE_PLACEMENT_REASONS)[number];

const DeclinePlacementResultSchema = z.object({
  proposal_id: z.string(),
  status: z.literal('rejected'),
  reason: z.enum(DECLINE_PLACEMENT_REASONS),
  text: z.string().nullable(),
});

const BulkPlacementResultSchema = z.object({
  items: z.array(
    z.discriminatedUnion('outcome', [
      z.object({
        proposal_id: z.string(),
        outcome: z.literal('confirmed'),
        result: ConfirmPlacementResultSchema,
      }),
      z.object({
        proposal_id: z.string(),
        outcome: z.literal('skipped'),
        reason: z.string(),
        message: z.string(),
      }),
    ]),
  ),
  confirmed: z.number(),
  skipped: z.number(),
});
export type BulkPlacementResult = z.infer<typeof BulkPlacementResultSchema>;

/** The confirm body: every field optional; a placement confirmed as proposed sends `{}`. */
export interface ConfirmPlacementBody {
  interview_id?: string | null;
  interview_type_id?: string | null;
  area_ids?: string[];
  people?: Array<
    | { person_id: string }
    | {
        full_name: string;
        title?: string;
        department?: string;
        email?: string;
        match_decision?:
          | { decision: 'different'; candidate_ids: string[] }
          | { decision: 'same'; person_id: string };
      }
  >;
  duration_minutes?: number;
}

const post = <T>(path: string, schema: z.ZodType<T>, body: unknown = {}) =>
  apiClient(path, schema, { method: 'POST', body, idempotencyKey: newIdempotencyKey() });

export const listInterviewProposals = (e: string) =>
  apiClient(base(e), InterviewProposalListSchema);
export const confirmInterviewProposal = (e: string, id: string, body: ConfirmPlacementBody = {}) =>
  post(`${base(e)}/${seg(id)}:confirm`, ConfirmPlacementResultSchema, body);
export const declineInterviewProposal = (
  e: string,
  id: string,
  body: { reason: DeclinePlacementReason; text?: string },
) => post(`${base(e)}/${seg(id)}:decline`, DeclinePlacementResultSchema, body);
export const confirmInterviewProposals = (e: string, ids: string[]) =>
  post(`${base(e)}:confirm`, BulkPlacementResultSchema, { ids });

export const placementKeys = {
  list: (e: string) => [...discoveryKeys.all(e), 'interview-proposals'] as const,
};

export function useInterviewProposalsQuery(e: string) {
  return useQuery({
    queryKey: placementKeys.list(e),
    queryFn: () => listInterviewProposals(e),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Bring in files (GET /discovery/intake): what became of each file
// ---------------------------------------------------------------------------

export const IntakeFileSchema = z.object({
  document_id: z.string(),
  title: z.string(),
  source_party: z.string(),
  classified_type: z.string().nullable(),
  created_at: z.string(),
  state: z.enum(['placed', 'to_place', 'sorting', 'reading', 'read', 'skipped', 'failed']),
  reason: z.string().nullable(),
  proposal_id: z.string().nullable(),
  interview: z
    .object({ id: z.string(), code: z.string(), title: z.string(), person: z.string().nullable() })
    .nullable(),
  proposed: z.number(),
  confirmed: z.number(),
});
export type IntakeFile = z.infer<typeof IntakeFileSchema>;

const IntakeSchema = z.object({ files: z.array(IntakeFileSchema) });

const intakeUrl = (e: string) => `/api/v1/engagements/${encodeURIComponent(e)}/discovery/intake`;

export const getIntake = (e: string) => apiClient(intakeUrl(e), IntakeSchema);

export const INTAKE_POLL_MS = 4000;

/**
 * The intake read. While a file is still being sorted or read, it asks
 * again every few seconds so the lists move on by themselves.
 */
export function useIntakeQuery(e: string, opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: [...discoveryKeys.all(e), 'intake'] as const,
    queryFn: () => getIntake(e),
    retry: false,
    refetchInterval: (q) =>
      opts.poll || q.state.data?.files.some((f) => f.state === 'sorting' || f.state === 'reading')
        ? INTAKE_POLL_MS
        : false,
  });
}
